/** Centralized React Query keys — extend per feature during rollout. */

export type PlansListFilters = {
  page: number;
  limit: number;
  search?: string;
  isActive?: boolean;
  assignableOnly?: boolean;
};

export type CouponsListFilters = { affiliateId?: string };

export type CompaniesListFilters = {
  page: number;
  limit: number;
  search?: string;
  includeDeleted: boolean;
  includePurged: boolean;
};

export type ProductsListFilters = {
  page: number;
  limit: number;
  search?: string;
  active?: boolean;
  billingType?: string;
};

export type SubscriptionsListFilters = {
  search?: string;
  page: number;
  limit: number;
  companyId?: string;
  planId?: string;
  status?: string;
};

export type UsersListFilters = {
  page: number;
  limit: number;
  search?: string;
  role?: string;
  isActive?: boolean;
};

export type AnnouncementsListFilters = {
  page: number;
  limit: number;
  search?: string;
  status?: 'draft' | 'published' | 'archived';
};

export const queryKeys = {
  invoices: { all: ['invoices'] as const, list: (filters: object) => ['invoices', 'list', filters] as const },
  resendSettings: ['settings', 'resend'] as const,
  aiSettings: ['settings', 'ai'] as const,
  metaSettings: ['settings', 'meta'] as const,
  whatsappPricing: ['settings', 'whatsapp-pricing'] as const,
  metaAvailability: ['settings', 'meta-availability'] as const,
  metaOverride: (companyId: string) => ['settings', 'meta-override', companyId] as const,
  modulePackages: ['module-packages'] as const,
  financialPolicies: ['settings', 'financial-policies'] as const,
  platformSettings: ['settings', 'platform'] as const,
  brandingSettings: ['settings', 'branding'] as const,
  billingSettings: ['settings', 'gateways'] as const,
  affiliateProgram: ['settings', 'affiliate-program'] as const,
  plans: {
    capabilities: ['plans', 'capabilities'] as const,
    configuration: (id: string) => ['plans', 'configuration', id] as const,
    prices: (id: string) => ['plans', 'prices', id] as const,
    all: ['plans'] as const,
    lists: () => [...queryKeys.plans.all, 'list'] as const,
    list: (filters: PlansListFilters) => [...queryKeys.plans.lists(), filters] as const,
    details: () => [...queryKeys.plans.all, 'detail'] as const,
    detail: (id: string) => [...queryKeys.plans.details(), id] as const,
  },
  coupons: {
    all: ['coupons'] as const,
    list: (filters?: CouponsListFilters) => [...queryKeys.coupons.all, 'list', filters ?? {}] as const,
  },
  companies: {
    migrationCandidates: ['companies', 'migration-candidates'] as const,
    members: (id: string) => ['companies', 'members', id] as const,
    integrationEntitlements: (id: string) => ['companies', 'integration-entitlements', id] as const,
    licensing: (id: string) => ['companies', 'licensing', id] as const,
    invoices: (id: string) => ['companies', 'invoices', id] as const,
    all: ['companies'] as const,
    list: (filters: CompaniesListFilters) => [...queryKeys.companies.all, 'list', filters] as const,
    stats: () => [...queryKeys.companies.all, 'stats'] as const,
    detail: (companyId: string) => [...queryKeys.companies.all, 'detail', companyId] as const,
    detailStats: (companyId: string) => [...queryKeys.companies.all, 'detail-stats', companyId] as const,
    /** Picker lists (create company dialog, filters, announcement audience). */
    pickerList: (filters: { limit: number; page?: number; search?: string }) =>
      [...queryKeys.companies.all, 'picker', filters] as const,
  },
  products: {
    all: ['products'] as const,
    lists: () => [...queryKeys.products.all, 'list'] as const,
    list: (filters: ProductsListFilters) => [...queryKeys.products.lists(), filters] as const,
    /** Active catalog slice for subscription / company detail pickers. */
    catalogActive: () => [...queryKeys.products.all, 'catalog', 'active-200'] as const,
  },
  subscriptions: {
    overview: () => [...queryKeys.subscriptions.all, 'overview'] as const,
    all: ['subscriptions'] as const,
    lists: () => [...queryKeys.subscriptions.all, 'list'] as const,
    list: (filters: SubscriptionsListFilters) => [...queryKeys.subscriptions.lists(), filters] as const,
    byCompany: (companyId: string) => [...queryKeys.subscriptions.all, 'by-company', companyId] as const,
    items: (subscriptionId: string) => [...queryKeys.subscriptions.all, 'items', subscriptionId] as const,
  },
  users: {
    all: ['users'] as const,
    lists: () => [...queryKeys.users.all, 'list'] as const,
    list: (filters: UsersListFilters) => [...queryKeys.users.lists(), filters] as const,
  },
  modules: {
    all: ['modules'] as const,
    registry: () => [...queryKeys.modules.all, 'registry'] as const,
    availableForProduct: (productId: string | undefined) =>
      [...queryKeys.modules.all, 'available-for-product', productId ?? 'none'] as const,
  },
  whatsappServers: {
    all: ['whatsapp-servers'] as const,
    list: () => [...queryKeys.whatsappServers.all, 'list'] as const,
    stats: () => [...queryKeys.whatsappServers.all, 'stats'] as const,
  },
  affiliates: {
    all: ['affiliates'] as const,
    list: (withStats: boolean) => [...queryKeys.affiliates.all, 'list', { withStats }] as const,
  },
  dashboard: {
    all: ['dashboard'] as const,
    overview: () => [...queryKeys.dashboard.all, 'overview'] as const,
    serverStats: () => [...queryKeys.dashboard.all, 'server-stats'] as const,
  },
  usage: {
    all: ['usage'] as const,
    byCompany: (filters: { from?: string; to?: string; companyId?: string }) =>
      [...queryKeys.usage.all, 'by-company', filters] as const,
    series: (filters: { from?: string; to?: string; companyId?: string }) =>
      [...queryKeys.usage.all, 'series', filters] as const,
  },
  ph24Logs: {
    all: ['ph24-logs'] as const,
    page: (filters: Record<string, string | number | undefined>) => [...queryKeys.ph24Logs.all, 'page', filters] as const,
  },
  companyDeletions: {
    all: ['company-deletions'] as const,
    page: (filters: { page: number; limit: number; search?: string; status?: string }) =>
      [...queryKeys.companyDeletions.all, 'page', filters] as const,
  },
  staffAccessLogs: {
    all: ['staff-access-logs'] as const,
    page: (filters: { page: number; limit: number; staffEmail?: string }) =>
      [...queryKeys.staffAccessLogs.all, 'page', filters] as const,
  },
  migration: {
    all: ['migration'] as const,
    legacyClients: (filters: { page: number; limit: number; search?: string }) =>
      [...queryKeys.migration.all, 'legacy-clients', filters] as const,
    legacyClient: (id: string) => [...queryKeys.migration.all, 'legacy-client', id] as const,
    jobs: (filters: { limit?: number; status?: string } = {}) =>
      [...queryKeys.migration.all, 'jobs', filters] as const,
  },
  announcements: {
    all: ['announcements'] as const,
    lists: () => [...queryKeys.announcements.all, 'list'] as const,
    list: (filters: AnnouncementsListFilters) =>
      [...queryKeys.announcements.lists(), filters] as const,
    detail: (id: string) => [...queryKeys.announcements.all, 'detail', id] as const,
  },
} as const;
